import type { SourcingCompany } from '@dustin-web/microsite-config'
import Holidays from 'date-holidays'

/**
 * A mapping of market codes to their respective daily cutoff times (in 24-hour 'HH:mm' format).
 */
export const MARKET_CUTOFF_TIME = new Map<SourcingCompany, string>([
  ['SE', '16:00'],
  ['FI', '15:00'],
  ['NO', '15:00'],
  ['DK', '14:30'],
  ['NL', '16:30'],
])

/**
 * A mapping of market codes to their corresponding IANA time zone identifiers.
 */
const MARKET_TIMEZONE = new Map<SourcingCompany, string>([
  ['SE', 'Europe/Stockholm'],
  ['FI', 'Europe/Helsinki'],
  ['NO', 'Europe/Oslo'],
  ['DK', 'Europe/Copenhagen'],
  ['NL', 'Europe/Amsterdam'],
  // ['BE', 'Europe/Brussels'],
])

/**
 * Determines whether a shipping cutoff banner should be shown to the user
 * based on market-specific cutoff times and time zone alignment.
 *
 * The banner is only shown if:
 * - The current day is not a public holiday in the given market.
 * - The current day is a weekday (Monday–Friday).
 * - The user's browser time zone matches the market's designated time zone.
 * - The current local time (in the market time zone) is earlier than
 *   the market's configured cutoff time.
 *
 * @param market - The sourcing company or market identifier.
 *                 Used to look up the market's time zone and cutoff time.
 * @returns `true` if the banner should be displayed; otherwise `false`.
 *
 * @remarks
 * - Public holidays are resolved using the `date-holidays` library, initialised
 *   with the market code (e.g., `'SE'`, `'NO'`).
 * - Market time zones and cutoff times are sourced from the `MARKET_TIMEZONE`
 *   and `MARKET_CUTOFF_TIME` maps.
 * - Cutoff times should be defined in `"HH:mm"` 24-hour format (e.g., `"17:30"`).
 */
export const shouldShowBanner = (market: SourcingCompany): boolean => {
  const today = new Date()
  const hd = new Holidays(market)

  const isHoliday = !!hd.isHoliday(today)
  const isWeekend = today.getDay() === 0 || today.getDay() === 6

  if (isHoliday || isWeekend) {
    return false
  }

  const browserTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone
  const marketTimezone = MARKET_TIMEZONE.get(market)

  if (browserTimeZone !== marketTimezone) {
    return false
  }

  const userTime = today.toLocaleTimeString('en-US', {
    timeZone: marketTimezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const marketCutOffTime = MARKET_CUTOFF_TIME.get(market) ?? '0'

  // Convert both times to minutes for reliable comparison
  const [userHours, userMinutes] = userTime.split(':').map(Number)
  const [cutoffHours, cutoffMinutes] = marketCutOffTime.split(':').map(Number)

  const userTimeInMinutes = userHours * 60 + userMinutes
  const cutoffTimeInMinutes = cutoffHours * 60 + cutoffMinutes

  return userTimeInMinutes < cutoffTimeInMinutes
}
